import {IRevolutPaymentControllerViewModel,} from "./revolut-payment-controller.view-model.interface";
import {BookingModel} from "../../../booking.model";
import {
    RevolutCheckoutError,
    RevolutPayEventPayload,
    RevolutPaymentsModuleInstance,
    WidgetPaymentsRevolutPayOptions
} from "@revolut/checkout";
import {IServiceFactory} from "../../../../../service-factory.interface";
import {PaymentMethodCodesEnum} from "../payment-method-codes.enum";
import {ValidationResultEnum} from "../../../../../../types/validation-result.enum";
import {
    IRevolutPayDeepLinkListenerSubscription,
    RevolutPayCallbackType,
    RevolutPayDeepLinkHandler
} from "../../../../../deep-links/handlers/revolut-pay-deep-link.handler";


declare type RevolutPayDropOffState = 'enter_otp' | 'payment_summary' | 'load_session_data' | 'enter_card_details' | 'verify_user_details' | 'enter_personal_details' | 'enter_shipping_details' | 'revolut_app_push_challenge';

export class RevolutPaymentControllerModel implements IRevolutPaymentControllerViewModel {
    constructor(private readonly revolutPaymentModule: RevolutPaymentsModuleInstance,
                private readonly paymentMethodCode: PaymentMethodCodesEnum,
                private readonly booking: BookingModel) {
        this.revolutPaymentModule.revolutPay.on("payment", this._handlePaymentEvent);
        this.revolutPaymentModule.revolutPay.on("click", this._onClick);
        this._revolutPayDeepLinkHandler = this.services.deepLinks.getDeepLinkHandler(RevolutPayDeepLinkHandler);
        this._revolutPayDeepLinkSubscription = this._revolutPayDeepLinkHandler.registerListener(this._handleRevolutPayDeepLink);
    }

    private readonly _revolutPayDeepLinkSubscription: IRevolutPayDeepLinkListenerSubscription;


    private get services(): IServiceFactory {
        return this.booking.services;
    }

    mount(element: HTMLElement): void {
        this.revolutPaymentModule.revolutPay.mount(element, this._getButtonOptions())
    }

    dispose(): void {
        this._revolutPayDeepLinkSubscription.unsubscribe();
        this.revolutPaymentModule.revolutPay.destroy();
        this.revolutPaymentModule.destroy();
    }

    private readonly _revolutPayDeepLinkHandler: RevolutPayDeepLinkHandler;

    private _onClick = () => {
        if(this.booking.validateTravelConditionsAcceptance() !== ValidationResultEnum.Success) {
            throw new Error(this.booking.travelConditionsAcceptanceError!);
        }
    }

    private _handleRevolutPayDeepLink = async (callbackType: RevolutPayCallbackType)=> {
        if(callbackType === RevolutPayCallbackType.Cancel) {
            await this.booking.paymentHandler.cancelPspOrder();
        }

    }

    private _getButtonOptions(): WidgetPaymentsRevolutPayOptions {
        const typedOptions: WidgetPaymentsRevolutPayOptions = {
            createOrder: this._createOrder,
            currency: this.booking.currency,
            totalAmount: this.booking.balanceDue.amountToCents(),
            buttonStyle: {
                action: 'pay',
                cashback: false
            },
            validate: async () => {
                const validationResult = await this.booking.paymentHandler.performStartBookingPaymentValidations();
                return validationResult.result === ValidationResultEnum.Success;
            },
            ...this._revolutPayDeepLinkHandler.getMobileRedirectUrls()
        };

        let untypedOptions: any = typedOptions; //because appReturnUrl parameter doesn't exist in the type definition for WidgetPaymentsRevolutPayOptions

        if(this.services.device.isHybrid) {
            untypedOptions.appReturnUrl = this._revolutPayDeepLinkHandler.getAppReturnUrl()
        }

        return untypedOptions;
    }

    private _createOrder = async (): Promise<{publicId: string}> => {

        const startBookingPaymentResult = await this.booking.paymentHandler.startBookingPayment({
            paymentMethodCode: this.paymentMethodCode
        });

        return {
            publicId: startBookingPaymentResult.pspOrderToken ?? ''
        };
    }

    private _handlePaymentEvent = async (payload: RevolutPayEventPayload<"payment">): Promise<void> => {
        const payloadType = payload.type;
        switch (payloadType) {
            case 'success':
                await this._onSuccess();
                break;
            case 'cancel':
                await this._onCancel(payload.dropOffState);
                break;
            case 'error':
                await this._onError(payload.error);
                break;
            default:
                this.services.logger.error(`Unknown Revolut payment event type ${payloadType}`)
                break;
        }
    }

    private async _onSuccess(): Promise<void> {
        await this.booking.paymentHandler.finalizePspOrder();
    }

    private async _onCancel(dropOffState: RevolutPayDropOffState): Promise<void> {
        this.services.logger.debug(`Revolut payment canceled. dropOffState = ${dropOffState}`);
        await this.booking.paymentHandler.cancelPspOrder();
    }

    private async _onError(error: RevolutCheckoutError): Promise<void> {
        await this.booking.paymentHandler.finalizePspOrder({
            dontShowErrorMessage: true
        });
    }


}